import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  BASE_URL_SANDBOX,
  DELETE_CARD,
  GET_ALL_USER_CARDS,
} from '../../../Constants/Config';

const initialState = {
  loading: false,
  userCards: [],
  error: '',
  requestKey: '',
  activeRequestId: null,
  activeRequestKey: '',
};

export const createUserCardsRequestKey = (data) => {
  const userId = data?.user_id ?? data?.cid ?? data?.customer_id;
  const merchantId = data?.merchant_id;
  if (
    userId === null ||
    userId === undefined ||
    merchantId === null ||
    merchantId === undefined
  ) {
    return '';
  }

  const normalizedUserId = String(userId).trim();
  const normalizedMerchantId = String(merchantId).trim().toUpperCase();
  return normalizedUserId && normalizedMerchantId
    ? JSON.stringify([normalizedUserId, normalizedMerchantId])
    : '';
};

// Generate pening , fulfilled and rejected action type
export const fetchAllUserCards = createAsyncThunk(
  'userCards/fetchAllUserCards',
  async (data, { rejectWithValue }) => {
    try {
      const token = Cookies.get('login_token');
      const response = await axios.post(
        BASE_URL_SANDBOX + GET_ALL_USER_CARDS,
        data,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}`, } },
      );

      if (response.data.status === 200) {
        return response.data.result;
      }
      return rejectWithValue({
        message: response?.data?.message || 'Unable to load saved cards',
      });
    } catch (error) {
      return rejectWithValue({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Unable to load saved cards',
      });
    }
  },
);

export const deleteSingleCard = createAsyncThunk(
  'userCards/deleteSingleCard',
  async (data) => {
    try {
      const response = await axios.post(BASE_URL_SANDBOX + DELETE_CARD, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status === 200) {
        return data;
      }
    } catch (error) {
      throw new Error(error.response.data.message);
    }
  },
);

const userCardsSlice = createSlice({
  name: 'userCards',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchAllUserCards.pending, (state, action) => {
      const requestId = action.meta?.requestId;
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (!requestId || !requestKey) {
        state.loading = false;
        state.userCards = [];
        state.requestKey = '';
        state.activeRequestId = null;
        state.activeRequestKey = '';
        return;
      }

      if (state.requestKey !== requestKey) {
        state.userCards = [];
        state.requestKey = '';
      }
      state.loading = true;
      state.error = '';
      state.activeRequestId = requestId;
      state.activeRequestKey = requestKey;
    });
    builder.addCase(fetchAllUserCards.fulfilled, (state, action) => {
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.userCards = Array.isArray(action.payload) ? action.payload : [];
      state.error = '';
      state.requestKey = requestKey;
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });
    builder.addCase(fetchAllUserCards.rejected, (state, action) => {
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (
        action.meta?.requestId !== state.activeRequestId ||
        requestKey !== state.activeRequestKey
      ) {
        return;
      }

      state.loading = false;
      state.userCards = [];
      state.error =
        action.payload?.message ||
        action.error?.message ||
        'Unable to load saved cards';
      state.requestKey = '';
      state.activeRequestId = null;
      state.activeRequestKey = '';
    });

    builder.addCase(deleteSingleCard.pending, (state, action) => {
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (!requestKey || requestKey !== state.requestKey) return;

      state.loading = true;
      state.error = '';
    });
    builder.addCase(deleteSingleCard.fulfilled, (state, action) => {
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (!requestKey || requestKey !== state.requestKey) return;

      const paymentMethodKey = action.payload?.paymethod_key;
      state.loading = false;
      if (paymentMethodKey === null || paymentMethodKey === undefined) {
        state.error = 'Unable to delete saved card';
        return;
      }

      state.userCards = (Array.isArray(state.userCards)
        ? state.userCards
        : []
      ).filter((item) => item?.key !== paymentMethodKey);
      state.error = '';
    });
    builder.addCase(deleteSingleCard.rejected, (state, action) => {
      const requestKey = createUserCardsRequestKey(action.meta?.arg);
      if (!requestKey || requestKey !== state.requestKey) return;

      state.loading = false;
      state.userCards = Array.isArray(state.userCards) ? state.userCards : [];
      state.error = action.error?.message || 'Unable to delete saved card';
    });
  },
});

export default userCardsSlice.reducer;
